/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The picture on a recipe.
 *
 * The endpoint is behind a bearer token, so this cannot be a plain <img src>.
 * The bytes are fetched, turned into an object URL, and the URL is revoked on
 * unmount — without that, scrolling a long library leaks a blob per card for
 * the lifetime of the tab. Same contract as CirclePhoto.
 *
 * ── IT RENDERS NOTHING WHEN THERE IS NO PICTURE ─────────────────────────────
 * Deliberately not a placeholder. Most recipes in a real library will never
 * have a photo — they were typed in, or imported from a site with no image —
 * and a grid of grey "no image" boxes is worse than a grid of text, because it
 * reads as broken rather than as plain.
 *
 * So the caller checks `hasImage` and picks a layout: the image layout when
 * there is one, the text layout when there isn't. That is the whole reason the
 * server sends `has_image` on the listing rather than making clients discover
 * it by requesting bytes that 404.
 */

import { useEffect, useState } from "react";

import { RecipesApi } from "../../api/endpoints.ts";

type Aspect = "hero" | "wide" | "square" | "thumb";

const ASPECT: Record<Aspect, string> = {
  hero: "aspect-[3/2] w-full",
  wide: "aspect-[4/3] w-full",
  square: "aspect-square w-full",
  thumb: "h-16 w-16",
};

export function RecipeImage({ recipeId, alt = "", aspect = "wide", className = "", rounded = true }: {
  recipeId: string;
  /** The dish name. Empty when a caption beside it already names the recipe. */
  alt?: string;
  aspect?: Aspect;
  className?: string;
  rounded?: boolean;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    setUrl(null);
    setFailed(false);

    RecipesApi.imageUrl(recipeId)
      .then((u) => {
        // The effect may have been torn down while the fetch was in flight.
        // Revoking immediately keeps that case from leaking.
        if (revoked) { URL.revokeObjectURL(u); return; }
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => setFailed(true));

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [recipeId]);

  const box = `${ASPECT[aspect]} ${rounded ? "rounded-xl2" : ""} overflow-hidden bg-paper-3`;

  // A failed fetch collapses to nothing rather than showing a broken-image
  // affordance. The layout around it already works without a picture.
  if (failed) return null;
  if (!url) return <div className={`${box} animate-pulse ${className}`} />;

  return (
    <div className={`${box} ${className}`}>
      <img src={url} alt={alt} loading="lazy" className="h-full w-full object-cover" />
    </div>
  );
}
